import { IPivot, Icon, mergeStyles } from '@fluentui/react';
import * as React from 'react';
import { capturePageAction, logClientDebuggingEvent } from '../../../../web/utils/oneDsUtil';
import { AutomationIds } from '../../../models/constants/automationIds';
import {
  Environment,
  IconNames,
  SourceApps,
} from '../../../models/constants/constants';
import {
  UITabView,
  IUITabViewInfo,
  UITabViewInfo,
} from '../../../models/reduxState';
import { FreelanceRequest } from '../models/requestModels';
import {
  IMainNavigationStyles,
  getMainNavClassNames,
  getNavIconStyles,
} from '../styles/appStyles';

import { IStyledComponent } from '../styles/themesUI';
import { getString } from '../../../../web/utils/stringLoader';

interface INavProps {
  showSupportNav: boolean;
  useIconsForSupportNav: boolean;
  isSignedIn: boolean;
  currentUITabView: UITabView;
  showHistoryTab: boolean;
  appVer: string;
  appName: string;
  sourceApp: SourceApps;
  market: string;
  isNonAgave: boolean;
  environment: Environment;
  isSupportTabEnabled: boolean;
  createdRequest: FreelanceRequest;
  isSearchSessionResetEnabled: boolean;
  isTeams: boolean;
  isCMCRequestCreationDeeplink: boolean;
  isRequestCreationTabHidden: boolean;
  // dispatchers
  updateUITabViewInfo: (uiTabViewInfo: IUITabViewInfo, requestCreated?: boolean) => void;
  resetSearchSession: () => void;
}

export type IAppNavProps = INavProps & IStyledComponent<IMainNavigationStyles>;

export class Navigation extends React.PureComponent<IAppNavProps> {
  private navigationTabRef = React.createRef<IPivot>();
  private tabRefs = {};
  public constructor(props: IAppNavProps) {
    super(props);
  }

  public componentDidMount(): void {
    const {
      isNonAgave,
      isSignedIn,
      isSupportTabEnabled,
      showHistoryTab,
      showSupportNav,
      useIconsForSupportNav,
      appVer,
      sourceApp,
      market,
      environment,
      currentUITabView,
      isCMCRequestCreationDeeplink,
    } = this.props;
    const navProps = {
      isNonAgave,
      isSignedIn,
      isSupportTabEnabled,
      showHistoryTab,
      showSupportNav,
      useIconsForSupportNav,
      appVer,
      sourceApp,
      market,
      environment,
      currentUITabView,
      isCMCRequestCreationDeeplink,
    };
    logClientDebuggingEvent(
      300,
      'maxfunnel',
      'Navigation',
      'navigationSection',
      'Navigation.tsx',
      'componentDidMount',
      'SupportNavigation',
      JSON.stringify(navProps)
    );

    if (
      this.props.showSupportNav &&
      this.props.currentUITabView === UITabView.searchHelp &&
      (
        this.navigationTabRef?.current ||
        (this.props.isTeams && this.tabRefs[UITabView.searchHelp]?.current)
      )
    ) {
      if (this.navigationTabRef?.current) {
        this.navigationTabRef.current.focus();
      }
      if (this.props.isTeams && this.tabRefs[UITabView.searchHelp]?.current) {
        this.tabRefs[UITabView.searchHelp].current.focus();
      }
    }
  }

  public componentDidUpdate = (prevProps: IAppNavProps): void => {
    /**
     * We reset the previous session after ticket creation, to start a new impression and disable stickyFooter
     * After ticket creation ContactSupport tab is hidden once the user changes tabs
     * We assume that since the user created a ticket the previous self help information is not needed
     */
    if (
      this.props.isSearchSessionResetEnabled &&
      this.props.showSupportNav &&
      !this.props.isSupportTabEnabled &&
      prevProps.isSupportTabEnabled &&
      this.props.createdRequest &&
      this.props.createdRequest.RequestId
    ) {
      this.props.resetSearchSession();
    }
    /**
     * We check if the showSupportNav state is changed to true, as it is not always true when the component is mounted
    * */
    else if (
      prevProps.showSupportNav !== this.props.showSupportNav &&
      this.props.showSupportNav &&
      this.navigationTabRef &&
      this.props.currentUITabView === UITabView.searchHelp &&
      this.props.isTeams
    ) {
      this.tabRefs[UITabView.searchHelp].current.focus();
      this.tabRefs[UITabView.searchHelp].current.blur();
    }
  };

  public render(): React.ReactNode {
    if (!this.props.showSupportNav) {
      return null;
    }

    const navClassNames = getMainNavClassNames(this.props.styles, {
      theme: this.props.theme,
      appName: this.props.appName,
      isNonAgave: this.props.isNonAgave,
    });

    return (
      <div className={navClassNames.navContainer}>
        <div role="tablist" id="tab_container" className={navClassNames.tabContainer}>{this._getTabs()}</div>
      </div>
    );
  }

  private _getTabs = () => {
    const { currentUITabView, useIconsForSupportNav } = this.props;
    const rcEnabled =
      (this.props.isSupportTabEnabled && !this.props.isRequestCreationTabHidden && this.props.isSignedIn) ||
      this.props.isCMCRequestCreationDeeplink;
    const thEnabled = this.props.showHistoryTab;
    const tabProps = {
      [UITabView.searchHelp]: useIconsForSupportNav
        ? IconNames.NeedHelpTabIconName
        : getString('navBar_searchHelp'),
      [UITabView.requestCreation]: useIconsForSupportNav
        ? IconNames.RequestCreationTabIconName
        : getString('navBar_requestCreation'),
      [UITabView.requestHistory]: useIconsForSupportNav
        ? IconNames.RequestHistoryTabIconName
        : getString('navBar_requestHistory'),
    };
    if (!rcEnabled) {
      tabProps[UITabView.requestCreation] = null;
    }
    if (!thEnabled) {
      tabProps[UITabView.requestHistory] = null;
    }
    const enableRHForContactSupport = (!rcEnabled && currentUITabView === UITabView.requestCreation) || currentUITabView === UITabView.requestDetails;
    const tabs: React.ReactNode[] = [] as React.ReactNode[];
    this.tabRefs = {};
    Object.keys(tabProps).map((key: UITabView) => {
      const isSelected =
        currentUITabView === key ||
        (enableRHForContactSupport &&
          key === UITabView.requestHistory);
      const className = mergeStyles(getNavIconStyles(this.props, isSelected));
      let elm = null;
      if (useIconsForSupportNav && tabProps[key]) {
        elm = <Icon iconName={tabProps[key]} />;
      } else {
        elm = tabProps[key];
      }

      // Assign a ref to each tab
      this.tabRefs[key] = React.createRef<HTMLSpanElement>();

      elm &&
        tabs.push(
          <span
            ref={this.tabRefs[key]}
            title={getString(`navBar_${UITabView[key]}`)}
            data-automation-id={this._getAutomationId(key)}
            onClick={this._onNavClick(key)}
            onKeyDown={this._onNavKeyPress(key)}
            className={className}
            tabIndex={0}
            role="tab"
            aria-controls="tab_container"
            aria-label={getString(`navBar_${UITabView[key]}`)}
            aria-selected={isSelected}
          >
            {elm}
          </span>
        );
    });
    return tabs;
  };

  private _changeTab = (tab: UITabView, requestId?: string, requestCreated?: boolean): void => {
    const uiTabViewInfo = new UITabViewInfo();
    uiTabViewInfo.uITabView = tab;
    uiTabViewInfo.requestId = requestId;
    this.props.updateUITabViewInfo(uiTabViewInfo, requestCreated);
  };

  private _onNavClick = (key: UITabView) => (): void => {
    if (key) {
      const logHandlerTags = {
        content: {
          contentId: 'onNavTabClick',
          contentName: 'onNavTabClick',
          formnm: 'maxFunnel',
          fileName: 'Navigation.tsx',
          methodName: '_onNavClick',
          areaName: 'supportNavigation',
          selectedUITabView: key,
        },
        behavior: 'CLICK',
      };
      capturePageAction(null, logHandlerTags);
      this._changeTab(key as UITabView);
    }
  };

  private _onNavKeyPress = (key: UITabView) => (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const lastTab = Object.keys(this.tabRefs).pop();

    if (['Enter', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      let tab = key;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        tab = this.findNextValidTab(event.key, key);

        if (tab !== key) {
          const nextElement = this.tabRefs[tab]?.current;
          if (nextElement instanceof HTMLElement) {
            nextElement.focus();
          }
        }
      }

      if (tab) {
        const logHandlerTags = {
          content: {
            contentId: 'onNavKeyPress',
            contentName: 'onNavKeyPress',
            formnm: 'maxFunnel',
            fileName: 'Navigation.tsx',
            methodName: '_onNavKeyPress',
            areaName: 'supportNavigation',
            selectedUITabView: key,
          },
          behavior: 'KeyPress',
        };
        capturePageAction(null, logHandlerTags);
        this._changeTab(tab as UITabView);
      }
    }
  };

  private findNextValidTab(direction: string, key: UITabView): UITabView {
    const tabViews = Object.keys(this.tabRefs);
    const currentIndex = tabViews.indexOf(key);
    const step = direction === 'ArrowLeft' ? -1 : 1;

    for (let i = currentIndex + step; i >= 0 && i < tabViews.length; i += step) {
      const nextTabKey = tabViews[i];
      if (this.tabRefs[nextTabKey]?.current) {
        return nextTabKey as UITabView;
      }
    }

    return key;
}

  private _getAutomationId = (icon: UITabView): string => {
    let id: string = String();
    switch (icon) {
      case UITabView.searchHelp:
        id = AutomationIds.SearchHelpTab;
        break;
      case UITabView.requestCreation:
        id = AutomationIds.RequestCreationTab;
        break;
      case UITabView.requestHistory:
        id = AutomationIds.RequestHistoryTab;
        break;
    }
    return id;
  };
}
